import { useMemo, useState } from "react";
import { FaPlus } from "react-icons/fa";
import { FiImage } from "react-icons/fi";

import { BannersAdmin } from "../../../models/BannersModels";
import { useAdminApi } from "../Admin/hooks/useAdminApi";
import { useAdminDelete } from "../Admin/hooks/useAdminDelete";

import BannersTable from "./components/BannersTable";
import Button from "../../../components/Atoms/Button";
import { Modal } from "../../../components/Atoms/Modal";
import {
  PageHeader,
  EmptyState,
  ErrorState,
  SkeletonTable,
  Confirm,
  useAdminResource,
} from "../../../components/Admin";
import AddBanner from "./components/AddBanner";

import { ListContainer } from "../../_layout/AdminLayout.styles";

/** Ordena pela posição na home; sem posição vai para o fim. */
const byPosition = (a: BannersAdmin, b: BannersAdmin) =>
  (Number(a.position) || Infinity) - (Number(b.position) || Infinity);

const AdminBanners = () => {
  const { fetchBanners } = useAdminApi();
  const { deleteItem, deletingId } = useAdminDelete();

  const { data, setData, isLoading, loadError, reload } = useAdminResource<
    BannersAdmin,
    unknown
  >({
    fetchPage: () => fetchBanners(),
    select: (response) => (Array.isArray(response) ? response : []),
    errorMessage: "Não foi possível buscar a lista de banners.",
  });

  const [openModal, setOpenModal] = useState<boolean>(false);
  const [pendingBanner, setPendingBanner] = useState<BannersAdmin | null>(null);

  const banners = useMemo(() => [...data].sort(byPosition), [data]);

  const handleModalState = () => setOpenModal((prevState) => !prevState);

  const confirmDelete = async () => {
    const banner = pendingBanner;
    setPendingBanner(null);
    if (!banner) return;

    const done = await deleteItem({
      entity: "banner",
      id: banner.bannerid,
      successTitle: "Banner excluído",
    });

    if (done) {
      setData((items) => items.filter((item) => item.bannerid !== banner.bannerid));
    }
  };

  const showInitialLoading = isLoading && data.length === 0 && !loadError;

  return (
    <>
      <PageHeader
        title="Banners"
        subtitle="Imagens em destaque no topo da home dos alunos."
        actions={
          <Button onClick={handleModalState}>
            <FaPlus />
            Novo Banner
          </Button>
        }
      />

      <ListContainer>
        {showInitialLoading ? (
          <SkeletonTable rows={3} columns={5} />
        ) : loadError ? (
          <ErrorState message={loadError} onRetry={reload} />
        ) : banners.length === 0 ? (
          <EmptyState
            icon={<FiImage size={40} />}
            title="Nenhum banner cadastrado"
            description="Adicione um banner para destacar um curso ou campanha na home."
            action={
              <Button onClick={handleModalState}>
                <FaPlus />
                Criar banner
              </Button>
            }
          />
        ) : (
          <BannersTable
            data={banners}
            onDelete={setPendingBanner}
            deletingId={deletingId}
          />
        )}
      </ListContainer>

      <Modal isOpen={openModal}>
        <AddBanner onClose={handleModalState} refreshFeth={reload} />
      </Modal>

      <Confirm
        open={pendingBanner !== null}
        danger
        title="Excluir banner"
        description="O banner some da home na hora. Não dá para desfazer."
        confirmLabel="Excluir"
        loading={!!deletingId}
        onConfirm={confirmDelete}
        onCancel={() => setPendingBanner(null)}
      />
    </>
  );
};

export default AdminBanners;
